import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { X, AlertTriangle, Check, ArrowRight } from 'lucide-react';
import { API } from '../context/AdminAuthContext';

/**
 * ShortfallFillModal (Feb 2026)
 * -----------------------------
 * Additive top-up for ONE shortfall / unassigned pick line on an
 * order. Scoped surgical: the user picks one or more pallets of the
 * SAME product and types a per-pallet qty. On save, the backend
 * turns the chosen qtys into fresh placement-backed pick_lines and
 * reduces (or clears) the matching shortfall row. Every other line
 * on the order is left untouched.
 *
 * Server endpoints:
 *   GET  /admin/orders/{order_id}/dispatch/shortfall-candidates/{line_id}
 *   POST /admin/orders/{order_id}/dispatch/fill-shortfall
 *
 * Props:
 *   order          — order doc (needs at least `id`)
 *   row            — the shortfall allocation-details row (needs
 *                    `order_line_id`, `item_name`, `item_code`,
 *                    `shortfall_qty`)
 *   authHeaders    — admin auth headers
 *   onClose        — close handler
 *   onDone         — called with the server response on success
 */
const ShortfallFillModal = ({ order, row, authHeaders, onClose, onDone }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [shortfallQty, setShortfallQty] = useState(row?.shortfall_qty || 0);
  // qtys keyed by placement_id — '' = empty input.
  const [qtyByPallet, setQtyByPallet] = useState({});
  const [confirmStep, setConfirmStep] = useState(false);

  const lineId = row?.order_line_id || '';
  const orderId = order?.id || '';

  useEffect(() => {
    if (!orderId || !lineId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const r = await axios.get(
          `${API}/admin/orders/${orderId}/dispatch/shortfall-candidates/${lineId}`,
          { headers: authHeaders },
        );
        if (cancelled) return;
        setCandidates(Array.isArray(r.data?.candidates) ? r.data.candidates : []);
        setShortfallQty(Number(r.data?.shortfall_qty || 0));
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.detail || 'Failed to load candidate pallets.');
          setCandidates([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orderId, lineId, authHeaders]);

  const chosen = useMemo(() => {
    const out = [];
    for (const c of candidates) {
      const raw = qtyByPallet[c.placement_id];
      const q = Number(raw);
      if (Number.isFinite(q) && q > 0) {
        out.push({ ...c, requested_qty: Math.floor(q) });
      }
    }
    return out;
  }, [candidates, qtyByPallet]);

  const totalChosen = chosen.reduce((s, c) => s + c.requested_qty, 0);
  const remainingShortfall = Math.max(0, shortfallQty - totalChosen);
  const anyExceeds = chosen.some((c) => c.requested_qty > c.free_qty);
  const overAll = totalChosen > shortfallQty;
  const canConfirm = chosen.length > 0 && !anyExceeds && !overAll && totalChosen > 0;

  const setQty = (placementId, value) => {
    setQtyByPallet((prev) => {
      const next = { ...prev };
      if (value === '' || value == null) {
        delete next[placementId];
      } else {
        next[placementId] = value;
      }
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const r = await axios.post(
        `${API}/admin/orders/${orderId}/dispatch/fill-shortfall`,
        {
          order_line_id: lineId,
          allocations: chosen.map((c) => ({
            placement_id: c.placement_id,
            qty: c.requested_qty,
          })),
        },
        { headers: authHeaders },
      );
      if (onDone) onDone(r.data);
      onClose && onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to apply shortfall fill.');
      setConfirmStep(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      data-testid="shortfall-fill-modal"
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-neutral-500">
              Allocate shortfall · line {lineId || '—'}
            </div>
            <div className="text-base font-semibold text-[#0F172A]">
              {row?.item_name || row?.item_code || 'Product'}{' '}
              <span className="font-mono text-neutral-500 text-sm">
                {row?.item_code ? `(${row.item_code})` : ''}
              </span>
            </div>
            <div className="text-xs text-red-700 flex items-center gap-1 mt-0.5">
              <AlertTriangle className="w-3 h-3" />
              Shortfall: <strong className="font-bold">{shortfallQty}</strong> ctn
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-neutral-100"
            data-testid="shortfall-fill-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="text-sm text-neutral-500" data-testid="shortfall-fill-loading">
              Loading candidate pallets…
            </div>
          ) : error && !confirmStep ? (
            <div
              className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
              data-testid="shortfall-fill-error"
            >
              {error}
            </div>
          ) : candidates.length === 0 ? (
            <div className="text-sm text-neutral-500 italic" data-testid="shortfall-fill-empty">
              No pallets of this product currently have free stock. Receive new stock or
              release a lock, then try again.
            </div>
          ) : confirmStep ? (
            <ConfirmStep
              lineId={lineId}
              productName={row?.item_name || ''}
              shortfallBefore={shortfallQty}
              shortfallAfter={remainingShortfall}
              chosen={chosen}
              error={error}
            />
          ) : (
            <>
              <div className="text-xs text-neutral-500 mb-2">
                Pallets with free stock for this product, sorted by expiry (FEFO).
                Free = qty − max(allocated_qty, dispatch_locked_qty).
              </div>
              <table className="w-full text-xs" data-testid="shortfall-fill-candidates-table">
                <thead className="bg-neutral-50 text-neutral-600">
                  <tr>
                    <th className="text-left px-2 py-1.5">Pallet</th>
                    <th className="text-left px-2 py-1.5">Location</th>
                    <th className="text-left px-2 py-1.5">Prod date</th>
                    <th className="text-left px-2 py-1.5">Expiry</th>
                    <th className="text-right px-2 py-1.5">Free</th>
                    <th className="text-right px-2 py-1.5 w-24">Pick qty</th>
                  </tr>
                </thead>
                <tbody>
                  {candidates.map((c, idx) => {
                    const typed = qtyByPallet[c.placement_id] ?? '';
                    const typedNum = Number(typed);
                    const over = Number.isFinite(typedNum) && typedNum > c.free_qty;
                    return (
                      <tr
                        key={c.placement_id}
                        className={`border-t border-neutral-200 ${over ? 'bg-red-50' : ''}`}
                        data-testid={`shortfall-fill-candidate-row-${idx}`}
                      >
                        <td className="px-2 py-1.5 font-mono text-[#0F172A]">
                          {c.pallet_id || '—'}
                        </td>
                        <td className="px-2 py-1.5 text-neutral-700">
                          {[c.warehouse_name, c.zone, c.location_id].filter(Boolean).join(' › ') || '—'}
                        </td>
                        <td className="px-2 py-1.5 text-neutral-700">{c.production_date || '—'}</td>
                        <td className="px-2 py-1.5 text-neutral-700">{c.expiry_date || '—'}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums font-semibold">
                          {c.free_qty}
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          <input
                            type="number"
                            min="0"
                            max={c.free_qty}
                            step="1"
                            value={typed}
                            onChange={(e) => setQty(c.placement_id, e.target.value)}
                            className={`w-20 text-right px-1.5 py-0.5 border rounded ${over ? 'border-red-400' : 'border-neutral-300'}`}
                            data-testid={`shortfall-fill-qty-input-${idx}`}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="mt-3 flex items-center justify-between text-xs">
                <div className="text-neutral-600" data-testid="shortfall-fill-running-total">
                  Picked <strong>{totalChosen}</strong> of <strong>{shortfallQty}</strong> ctn
                  {overAll && (
                    <span className="ml-2 text-red-700 font-semibold">
                      — exceeds the shortfall!
                    </span>
                  )}
                  {anyExceeds && !overAll && (
                    <span className="ml-2 text-red-700 font-semibold">
                      — a pallet is over its free qty!
                    </span>
                  )}
                </div>
                <div className="text-neutral-500">
                  Remaining shortfall after fill:{' '}
                  <strong className="text-[#0F172A]">{remainingShortfall}</strong>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-neutral-200 flex items-center justify-end gap-2">
          {confirmStep ? (
            <>
              <button
                type="button"
                onClick={() => setConfirmStep(false)}
                disabled={saving}
                className="px-3 py-1.5 text-sm rounded border border-neutral-300 hover:bg-neutral-50 disabled:opacity-50"
                data-testid="shortfall-fill-back-btn"
              >
                Back
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="px-3 py-1.5 text-sm rounded bg-[#0078D4] text-white hover:bg-[#005A9E] disabled:opacity-50 flex items-center gap-1"
                data-testid="shortfall-fill-save-btn"
              >
                <Check className="w-4 h-4" />
                {saving ? 'Saving…' : 'Save fill'}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-1.5 text-sm rounded border border-neutral-300 hover:bg-neutral-50"
                data-testid="shortfall-fill-cancel-btn"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => setConfirmStep(true)}
                disabled={!canConfirm || loading}
                className="px-3 py-1.5 text-sm rounded bg-[#0078D4] text-white hover:bg-[#005A9E] disabled:opacity-50 flex items-center gap-1"
                data-testid="shortfall-fill-review-btn"
              >
                Review <ArrowRight className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const ConfirmStep = ({ lineId, productName, shortfallBefore, shortfallAfter, chosen, error }) => (
  <div data-testid="shortfall-fill-confirm">
    <div className="text-xs text-neutral-500 mb-2">
      Review the change to line <strong>{lineId}</strong> ({productName || 'product'}).
      No other line, pallet or order will be touched.
    </div>
    {error && (
      <div
        className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 mb-3"
        data-testid="shortfall-fill-confirm-error"
      >
        {error}
      </div>
    )}
    <div className="grid grid-cols-2 gap-3 text-xs mb-4">
      <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-3">
        <div className="uppercase tracking-wide text-neutral-500 text-[10px] mb-1">Before</div>
        <div className="text-red-700 font-bold text-base">{shortfallBefore} ctn short</div>
      </div>
      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
        <div className="uppercase tracking-wide text-emerald-700 text-[10px] mb-1">After</div>
        <div className="text-emerald-700 font-bold text-base">
          {shortfallAfter === 0 ? 'Fully filled' : `${shortfallAfter} ctn short`}
        </div>
      </div>
    </div>
    <div className="text-xs font-semibold text-neutral-700 mb-1">
      Pallets to lock ({chosen.length})
    </div>
    <table className="w-full text-xs" data-testid="shortfall-fill-confirm-table">
      <thead className="bg-neutral-50 text-neutral-600">
        <tr>
          <th className="text-left px-2 py-1.5">Pallet</th>
          <th className="text-left px-2 py-1.5">Location</th>
          <th className="text-right px-2 py-1.5">Qty</th>
        </tr>
      </thead>
      <tbody>
        {chosen.map((c) => (
          <tr key={c.placement_id} className="border-t border-neutral-200">
            <td className="px-2 py-1.5 font-mono">{c.pallet_id || '—'}</td>
            <td className="px-2 py-1.5 text-neutral-700">
              {[c.warehouse_name, c.zone, c.location_id].filter(Boolean).join(' › ') || '—'}
            </td>
            <td className="px-2 py-1.5 text-right tabular-nums font-semibold">
              {c.requested_qty}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default ShortfallFillModal;
